"use client";

import { useSectionViewEvent } from "@/hooks/use-section-view-event";
import { PI_SECTION } from "@/lib/analytics/schema";

const withoutPi = [
  "Endless distractions and recommendations pulling you away",
  "No structure — random one-offs, missing chapters",
  "Doubts pile up with no one to ask",
  "DPPs, notes and tests scattered across 10 tabs",
];

const withPi = [
  "Distraction-free — built only for learning",
  "Structured by subject, chapter and exam",
  "Ask AI any doubt, mid-lecture",
  "Lectures, notes, DPPs, tests — one place, one subscription",
];

export function WhyPiSection() {
  useSectionViewEvent({
    sectionId: "why-pi",
    sectionName: PI_SECTION.COMPARISON.name,
    sectionPosition: PI_SECTION.COMPARISON.position,
  });

  return (
    <section id="why-pi" className="bg-pi-bg">
      <div className="mx-auto max-w-[1200px] px-4 py-14 sm:px-6 md:py-20 lg:px-8 lg:py-24">
        <h2 className="m-0 mb-2 text-center text-[clamp(26px,4vw,34px)] font-semibold leading-tight text-pi-bright">
          Built for studying. Not for scrolling.
        </h2>
        <div className="mx-auto mt-10 grid max-w-[980px] grid-cols-1 gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-pi-border bg-pi-elevated p-7">
            <h3 className="mt-0 mb-5 text-base font-semibold leading-snug text-pi-dim">
              Studying
            </h3>
            <ul className="m-0 flex list-none flex-col gap-4 p-0">
              {withoutPi.map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 text-[15px] leading-normal text-pi-text"
                >
                  <span className="mt-0.5 flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[#2A2A30] text-pi-dim">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </span>
                  <span className="min-w-0 break-words">{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-pi-accent bg-pi-elevated p-7 shadow-[0_0_0_1px_rgba(90,75,218,0.3),0_20px_50px_rgba(90,75,218,0.12)]">
            <h3 className="mt-0 mb-5 text-base font-semibold leading-snug text-pi-accent-soft">
              Studying on Pi
            </h3>
            <ul className="m-0 flex list-none flex-col gap-4 p-0">
              {withPi.map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 text-[15px] leading-normal text-pi-bright"
                >
                  <span className="mt-0.5 flex size-[22px] shrink-0 items-center justify-center rounded-full bg-pi-success/15 text-pi-success">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                  <span className="min-w-0 break-words">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
